import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { backendUrl, frontendUrl } from '../config/environment';
import './LoginPage.css';

interface OAuthResponse {
  auth_link: string;
  session_id: string;
  status: string;
}

const LoginPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [sessionId, setSessionId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    // First, try to get session_id from URL query parameters
    const urlSessionId = searchParams.get('session_id');
    
    if (urlSessionId) {
      setSessionId(urlSessionId);
      // Store it in localStorage for future use
      localStorage.setItem('oauth_session_id', urlSessionId);
    } else {
      // If not in URL, try to get from localStorage
      const storedSessionId = localStorage.getItem('oauth_session_id');
      
      if (storedSessionId) {
        setSessionId(storedSessionId);
      } else {
        // Generate a new session ID if neither URL nor localStorage has one
        const generateSessionId = () => {
          return 'session_' + Math.random().toString(36).substr(2, 16) + Date.now().toString(36);
        };
        
        const newSessionId = generateSessionId();
        setSessionId(newSessionId);
        localStorage.setItem('oauth_session_id', newSessionId);
      }
    }
  }, [searchParams]);

  const handleLogin = async () => {
    if (!sessionId.trim()) {
      setError('Session ID is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.get<OAuthResponse>(
        `${backendUrl}/oauth/init_login/shareIndia?session_id=${sessionId}`
      );

      if (response.data.status === 'success') {
        // Store session ID in localStorage for later use
        localStorage.setItem('oauth_session_id', sessionId);
        
        // Redirect to the auth link
        window.location.href = response.data.auth_link;
      } else {
        setError('Failed to initialize OAuth login');
      }
    } catch (err) {
      console.error('OAuth initialization error:', err);
      setError('Failed to connect to OAuth service. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>UTrade MCP OAuth Login</h1>
          <p>Secure authentication for your trading dashboard</p>
        </div>

        <div className="login-form">
          <div className="form-group">
            <label htmlFor="sessionId">Session ID:</label>
            <input
              type="text"
              id="sessionId"
              value={sessionId}
              placeholder="Session ID (auto-generated or from URL)"
              disabled={true}
              readOnly={true}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            onClick={handleLogin}
            disabled={loading || !sessionId.trim()}
            className="login-button"
          >
            {loading ? 'Initializing...' : 'Login with ShareIndia'}
          </button>
        </div>

        <div className="login-info">
          <p>
            <strong>Hosted at:</strong> {frontendUrl.replace('https://', '')}
          </p>
          <p>
            <strong>OAuth Provider:</strong> ShareIndia
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 